// ===== useChatPhases.js =====
import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { getCurrentUser } from '@aws-amplify/auth';
import useSonnetChat from '../hooks/useSonnetChat';

const useChatPhases = () => {
  // Core state management
  const [currentPhase, setCurrentPhase] = useState('streamlined');
  const [businessDescription, setBusinessDescription] = useState('General business and market research assistant');
  const [reportS3Url, setReportS3Url] = useState('');
  const [userName, setUserName] = useState('');
  const [error, setError] = useState(null);
  const [chatError, setChatError] = useState(null);
  const [processingStage, setProcessingStage] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  // Session management
  const [sessionId, setSessionId] = useState(() => {
      const timestamp = Date.now();
      const random = Math.random().toString(36).substr(2, 9);
      return `session_${timestamp}_${random}`;
  });

  // Streamlined mode specific state
  const [streamlinedMode, setStreamlinedMode] = useState(true);
  const [reportContentCache, setReportContentCache] = useState('');
  const [reportHistory, setReportHistory] = useState([]);
  const [autoInitialized, setAutoInitialized] = useState(false);

  // Version control state
  const [versionHistory, setVersionHistory] = useState([]);
  const [currentVersionNumber, setCurrentVersionNumber] = useState(1);
  const [isVersionSidebarOpen, setIsVersionSidebarOpen] = useState(false);
  const [versionContent, setVersionContent] = useState(null);

  // Report retrieval
  const [savedReports, setSavedReports] = useState([]);
  const [savedReportContent, setSavedReportContent] = useState(null);
  const [isLoadingReports, setIsLoadingReports] = useState(false);

  // Sessions retrieval
  const [savedSessions, setSavedSessions] = useState([]);
  const [isLoadingSessions, setIsLoadingSessions] = useState(false);

  // Track processed message IDs
  const processedMessageIds = useRef(new Set());
  const [message, setMessage] = useState('');

  // Get the authenticated user's email
  useEffect(() => {
    const fetchUserEmail = async () => {
      try {
        const user = await getCurrentUser();
        const email = user?.signInDetails?.loginId || user?.username;
        if (email) {
          const normalizedEmail = email.trim().toLowerCase();
          setUserName(normalizedEmail);
          console.log("Authenticated user email:", normalizedEmail);
        } else {
          console.warn("No email found in user object:", user);
          setUserName(`user_${Date.now()}`);
        }
      } catch (err) {
        console.error("Failed to get current authenticated user:", err);
        setUserName(`guest_${Date.now()}`);
      }
    };
    fetchUserEmail();
  }, []);

  // Version response handler
  const handleVersionResponse = useCallback((response) => {
    console.log('Version response received:', response);

    switch (response.type) {
      case 'version-created':
        if (response.success) {
          setCurrentVersionNumber(response.versionNumber || currentVersionNumber + 1);
          if (response.version && response.s3Key) {
            setVersionHistory(prev => [...prev, {
              version: response.version,
              description: response.description,
              timestamp: response.timestamp,
              s3Key: response.s3Key,
              url: response.url
            }]);
          }
        } else {
          setError('Failed to create version: ' + (response.error || 'Unknown error'));
        }
        break;

      case 'version-loaded':
        if (response.success) {
          if (response.url) {
            fetchVersionContent(response.url, response.version);
          } else if (response.content) {
            setVersionContent(response.content);
            setCurrentVersionNumber(response.version);
          }
        } else {
          setError('Failed to load version: ' + (response.error || 'Unknown error'));
        }
        break;

      case 'version-list':
        if (response.success && Array.isArray(response.versions)) {
          setVersionHistory(response.versions);
        }
        break;

      case 'reports-list': {
        console.log(`📄 Received ${response.count} reports:`, (response.reports || []).map(r => r.reportTitle));
        setSavedReports(response.reports || []);
        setIsLoadingReports(false);
        break;
      }

      case 'report-content': {
        console.log(`📄 Report content received: ${response.found ? 'found' : 'not found'}`);
        if (response.found) {
          setSavedReportContent({
            sessionId: response.sessionId,
            content: response.content,
          });
        } else {
          setSavedReportContent({
            sessionId: response.sessionId,
            content: null,
            error: response.error || 'Report not found',
          });
        }
        break;
      }

      case 'report-deleted': {
        console.log(`🗑️ Report deleted: ${response.sessionId}, success: ${response.success}`);
        if (response.success) {
          setSavedReports(prev => prev.filter(r => r.sessionId !== response.sessionId));
          // If the deleted report is currently being viewed, close it
          if (savedReportContent?.sessionId === response.sessionId) {
            setSavedReportContent(null);
          }
        } else {
          console.error('Failed to delete report:', response.error);
        }
        break;
      }

      case 'sessions-list': {
        console.log(`📋 Received ${(response.sessions || []).length} sessions`);
        setSavedSessions(response.sessions || []);
        setIsLoadingSessions(false);
        break;
      }

      default:
        console.warn('Unknown version response type:', response.type);
    }
  }, [currentVersionNumber]);

  const fetchVersionContent = useCallback(async (url, versionNumber) => {
    try {
      const response = await fetch(url, {
        method: 'GET',
        headers: { 'Accept': 'text/plain, text/markdown, */*', 'Cache-Control': 'no-cache' },
        cache: 'no-cache',
        mode: 'cors'
      });
      if (response.ok) {
        const content = await response.text();
        setVersionContent(content);
        setCurrentVersionNumber(versionNumber);
      } else {
        setError(response.status === 403
          ? 'Version access expired. Please try loading the version again.'
          : 'Failed to load version content');
      }
    } catch (error) {
      console.error('Error fetching version content:', error);
      setError('Failed to load version content');
    }
  }, []);

  const processReportTags = useCallback((messageText, messageId) => {
      if (!messageText || typeof messageText !== 'string') return;
      if (processedMessageIds.current.has(messageId)) return;
      processedMessageIds.current.add(messageId);

      const hasAnyTags = 
        messageText.includes('<report_section>') || 
        messageText.includes('<full_report>') || 
        messageText.includes('<report_save>');
      
      if (!hasAnyTags) return;

      const reportSectionRegex = /<report_section>\s*(\{[\s\S]*?\})\s*<\/report_section>/gi;
      const fullReportRegex = /<full_report>\s*(\{[\s\S]*?\})\s*<\/full_report>/gi;
      const reportSaveRegex = /<report_save>\s*(\{[\s\S]*?\})\s*<\/report_save>/gi;
      let match;

      // ── Parse <full_report> tags ──
      while ((match = fullReportRegex.exec(messageText)) !== null) {
        try {
          const reportData = JSON.parse(match[1].trim());
          setReportHistory(prev => {
            if (prev.find(r => r.s3Key === reportData.s3Key)) return prev;
            return [...prev, {
              ...reportData,
              type: 'full_report',
              timestamp: reportData.timestamp || new Date().toISOString(),
              url: reportData.url || null
            }];
          });
        } catch (error) {
          console.error('Error processing full report tag:', error);
        }
      }

      // ── Parse <report_section> tags ──
      while ((match = reportSectionRegex.exec(messageText)) !== null) {
        try {
          const sectionData = JSON.parse(match[1].trim());
          setReportHistory(prev => {
            if (prev.find(r => r.s3Key === sectionData.s3Key)) return prev;
            return [...prev, {
              ...sectionData,
              type: 'section',
              timestamp: sectionData.timestamp || new Date().toISOString(),
              url: sectionData.url || null
            }];
          });
        } catch (error) {
          console.error('Error processing section tag:', error);
        }
      }

      // ── Parse <report_save> tags ──
      while ((match = reportSaveRegex.exec(messageText)) !== null) {
        try {
          const saveData = JSON.parse(match[1].trim());
          
          // Construct s3Key since the agent doesn't include it
          const s3Key = saveData.s3Key || 
            `app_reports/${userName.toLowerCase()}/${sessionId}_report.md`;
          
          setReportHistory(prev => {
            if (prev.find(r => r.s3Key === s3Key)) {
              return prev.map(r => 
                r.s3Key === s3Key 
                  ? { ...r, wordCount: saveData.wordCount, displayName: saveData.reportTitle }
                  : r
              );
            }
            return [...prev, {
              s3Key,
              reportTitle: saveData.reportTitle,
              displayName: saveData.reportTitle,
              wordCount: saveData.wordCount,
              sessionId: sessionId,
              userName: userName,
              type: 'full_report',
              timestamp: new Date().toISOString(),
              url: null,
              needsPresignedUrl: true,
            }];
          });
          
          console.log('✅ Processed report_save:', saveData.reportTitle, 'key:', s3Key);
        } catch (error) {
          console.error('Error processing report_save tag:', error);
        }
      }
    }, [userName, sessionId]);  // ← uses sessionId

  // Initialize SonnetChat
  const {
    sonnetMessages,
    sonnetTypingText,
    agentStatus,
    sonnetIsTyping,
    sonnetWsConnected,
    sonnetWsConnecting,
    sonnetSendMessage,
    sonnetSessionId,
    currentStatus,
    pingCount,
    requestUrlRefresh,
    registerUrlRefreshCallback,
    requestHistory,
    requestSessionsList,
    requestNewSession,
    requestReportsList,
    requestReportContent,
    deleteSession,
    deleteReport,     
  } = useSonnetChat({
    message,
    setMessage,
    setIsLoading,
    setError,
    setProcessingStage,
    isActive: true,
    userName: userName,
    sessionId: sessionId,
    onVersionResponse: handleVersionResponse
  });

  

  // Monitor messages for report tags
  const lastProcessedMessageId = useRef(null);

  useEffect(() => {
    if (sonnetMessages && sonnetMessages.length > 0) {
      const lastMessage = sonnetMessages[sonnetMessages.length - 1];
      if (!lastMessage.isUser && lastMessage.text && lastMessage.id && lastMessage.id !== lastProcessedMessageId.current) {
        lastProcessedMessageId.current = lastMessage.id;
        processReportTags(lastMessage.text, lastMessage.id);
      }
    }
  }, [sonnetMessages, processReportTags]);

  // Update report history when URLs arrive
  useEffect(() => {
    if (sonnetMessages && sonnetMessages.length > 0) {
      const lastMessage = sonnetMessages[sonnetMessages.length - 1];
      if (!lastMessage.isUser && lastMessage.text && lastMessage.text.includes('"url":')) {
        const reportSectionRegex = /<report_section>\s*(\{[\s\S]*?\})\s*<\/report_section>/gi;
        const fullReportRegex = /<full_report>\s*(\{[\s\S]*?\})\s*<\/full_report>/gi;
        let match;

        while ((match = fullReportRegex.exec(lastMessage.text)) !== null) {
          try {
            const reportData = JSON.parse(match[1].trim());
            if (reportData.url) {
              setReportHistory(prev => prev.map(report =>
                report.s3Key === reportData.s3Key && !report.url
                  ? { ...report, url: reportData.url, expiresAt: reportData.expiresAt }
                  : report
              ));
            }
          } catch (e) { /* ignore */ }
        }

        while ((match = reportSectionRegex.exec(lastMessage.text)) !== null) {
          try {
            const sectionData = JSON.parse(match[1].trim());
            if (sectionData.url) {
              setReportHistory(prev => prev.map(report =>
                report.s3Key === sectionData.s3Key && !report.url
                  ? { ...report, url: sectionData.url, expiresAt: sectionData.expiresAt }
                  : report
              ));
            }
          } catch (e) { /* ignore */ }
        }
      }
    }
  }, [sonnetMessages]);

  useEffect(() => {
      if (sonnetSessionId && sonnetSessionId !== sessionId) {
          console.log(`🔄 Session switched: ${sessionId} → ${sonnetSessionId}`);
          setSessionId(sonnetSessionId);
      }
  }, [sonnetSessionId]);

  // Auto-initialize
  useEffect(() => {
    if (streamlinedMode && sonnetWsConnected && userName && !autoInitialized) {
      setAutoInitialized(true);
      setCurrentPhase('streamlined');
      setError(null);
      setChatError(null);
      setIsLoading(false);
      setProcessingStage(null);
    }
  }, [streamlinedMode, sonnetWsConnected, userName, autoInitialized]);

  const handleCreateVersion = useCallback(async (description) => {
    if (!sonnetWsConnected) { setError('Not connected.'); return; }
    const versionRequest = description
      ? `Please generate an updated version of the current report. Description: "${description}"`
      : `Please generate an updated version of the current report with new insights`;
    sonnetSendMessage(versionRequest);
  }, [sonnetWsConnected, sonnetSendMessage]);

  const handleVersionSelect = useCallback(async (version) => {
      try {
        if (version.url) {
          await fetchVersionContent(version.url, version.version);
        } else if (version.s3Key) {
          sonnetSendMessage(JSON.stringify({
            action: 'load-version', versionId: version.version,
            s3Key: version.s3Key, userName: userName, sessionId: sessionId
          }));
        } else {
          setError('Version data incomplete');
        }
      } catch (error) {
        setError('Failed to load version');
      }
    }, [fetchVersionContent, sonnetSendMessage, userName, sessionId]);

  const handleLoadVersionHistory = useCallback(async () => {
    const versions = reportHistory.map((report, index) => ({
      version: index + 1, sessionId, timestamp: report.timestamp || new Date().toISOString(),
      description: report.displayName || `Report ${index + 1}`,
      s3Key: report.s3Key, url: report.url, type: report.type || 'report', expiresAt: report.expiresAt
    }));
    setVersionHistory(versions);
  }, [reportHistory, sessionId]);

  const isFormValid = useCallback(() => businessDescription.trim().length > 0, [businessDescription]);

  const generateReportMessage = useCallback(() => {
    return `Please generate a report providing information on the following subject: "${businessDescription.trim()}". ` +
      `This report is requested by user '${userName}' for session ID '${sessionId}'`;
  }, [businessDescription, userName, sessionId]);

  const startReportGeneration = useCallback(async () => {
    if (!streamlinedMode && !isFormValid()) { setError('Please describe your business.'); return; }
    if (!sonnetWsConnected) { setError('Not connected.'); return; }
    if (!userName || !sessionId) { setError('Auth not ready.'); return; }
    try {
      if (streamlinedMode) {
        setError(null); setChatError(null); setReportS3Url('');
        setIsLoading(false); setProcessingStage(null); setCurrentPhase('streamlined');
        return;
      }
      const reportPrompt = generateReportMessage();
      setError(null); setChatError(null); setReportS3Url('');
      setCurrentPhase('generating');
      setProcessingStage('Agent is analyzing your business and generating report...');
      setIsLoading(true);
      sonnetSendMessage(reportPrompt);
    } catch (err) {
      setError('Failed to start report generation.'); setIsLoading(false);
    }
  }, [streamlinedMode, isFormValid, sonnetWsConnected, userName, sessionId, generateReportMessage, sonnetSendMessage]);

  const resetToForm = useCallback(() => {
      processedMessageIds.current.clear();
      lastProcessedMessageId.current = null;
      setReportContentCache('');
      setReportHistory([]);
      setMessage('');
      setError(null);
      setChatError(null);
      setIsLoading(false);
      setProcessingStage(null);
      setVersionHistory([]);
      setCurrentVersionNumber(1);
      setIsVersionSidebarOpen(false);
      setVersionContent(null);
      setSavedReportContent(null);

      if (streamlinedMode) {
          if (sonnetWsConnected && requestNewSession) {
              requestNewSession();
              // The 'new_session' response will:
              // 1. Clear sonnetMessages in useSonnetChat
              // 2. Set new internalSessionId in useSonnetChat
              // 3. Sync to our sessionId via the useEffect (Fix 1)
          }
      } else {
          setBusinessDescription('');
          setCurrentPhase('form');
          setReportS3Url('');
      }
  }, [streamlinedMode, sonnetWsConnected, requestNewSession]);

  const handleSendChatMessage = useCallback((textToSend) => {
    const messageToSend = textToSend || message;
    if (messageToSend.trim()) {
      sonnetSendMessage(messageToSend);
      if (!textToSend) setMessage('');
    }
  }, [sonnetSendMessage, message]);

  const formHandlers = useMemo(() => ({ setBusinessDescription }), []);

  const handleViewReport = useCallback((report) => {
    if (!sonnetWsConnected) {
      setError('Not connected. Please wait for reconnection.');
      return;
    }

    if (report.url && report.expiresAt && new Date(report.expiresAt) > new Date()) {
      // URL is still valid — open it directly
      window.open(report.url, '_blank');
      return;
    }

    // Request fresh presigned URL via WebSocket
    const s3Key = report.s3Key;
    console.log('🔗 Requesting presigned URL for:', s3Key);

    // Register a callback for when the URL comes back
    registerUrlRefreshCallback(s3Key, (result) => {
      if (result.success && result.url) {
        // Update the report in history with the new URL
        setReportHistory(prev => prev.map(r =>
          r.s3Key === s3Key
            ? { ...r, url: result.url, expiresAt: result.expiresAt }
            : r
        ));
        // Open the report
        window.open(result.url, '_blank');
      } else {
        setError('Failed to get report URL: ' + (result.error || 'Unknown error'));
      }
    });

    // Send the refresh request
    requestUrlRefresh(s3Key);
  }, [sonnetWsConnected, registerUrlRefreshCallback, requestUrlRefresh]);

  const getLatestReportContent = useCallback(() => {
    if (versionContent) return versionContent;
    if (reportHistory.length > 0) return reportHistory[reportHistory.length - 1].content;
    return reportContentCache;
  }, [reportHistory, reportContentCache, versionContent]);

  // ── Reports handlers ──
  const handleLoadSavedReports = useCallback(() => {
    if (!sonnetWsConnected) { console.warn('⚠️ handleLoadSavedReports: not connected'); return; }
    if (!userName) { console.warn('⚠️ handleLoadSavedReports: userName not ready'); return; }
    console.log('📄 Loading saved reports for:', userName);
    setIsLoadingReports(true);
    setSavedReportContent(null);
    requestReportsList();
    // Safety timeout
    setTimeout(() => {
      setIsLoadingReports(prev => {
        if (prev) { console.warn('⚠️ Reports loading timeout'); return false; }
        return prev;
      });
    }, 15000);
  }, [sonnetWsConnected, userName, requestReportsList]);

  const handleViewSavedReport = useCallback((reportSessionId) => {
    if (!sonnetWsConnected) return;
    setSavedReportContent(null);
    requestReportContent(reportSessionId);
  }, [sonnetWsConnected, requestReportContent]);

  const handleDeleteReport = useCallback((reportSessionId) => {
    if (!sonnetWsConnected) return;
    console.log('🗑️ Deleting report:', reportSessionId);
    deleteReport(reportSessionId);
  }, [sonnetWsConnected, deleteReport]);

  // ── Sessions handlers ──
  const handleLoadSessions = useCallback(() => {
    if (!sonnetWsConnected) { console.warn('⚠️ handleLoadSessions: not connected'); return; }
    if (!userName) { console.warn('⚠️ handleLoadSessions: userName not ready'); return; }
    console.log('📋 Loading sessions for:', userName);
    setIsLoadingSessions(true);
    requestSessionsList();
    // Safety timeout
    setTimeout(() => {
      setIsLoadingSessions(prev => {
        if (prev) { console.warn('⚠️ Sessions loading timeout'); return false; }
        return prev;
      });
    }, 15000);
  }, [sonnetWsConnected, userName, requestSessionsList]);

  const handleLoadSessionAndSwitch = useCallback((newSessionId) => {
      // Clear current state before loading new session
      setSavedReportContent(null);
      setReportHistory([]);
      setReportContentCache('');
      setVersionHistory([]);
      setVersionContent(null);
      processedMessageIds.current.clear();
      lastProcessedMessageId.current = null;
      
      // Clear error states
      setError(null);
      setChatError(null);
      setIsLoading(false);
      setProcessingStage(null);

      // Clear processed message tracking
      processedMessageIds.current.clear();
      lastProcessedMessageId.current = null;
      
      // Request history for the new session
      requestHistory(newSessionId);
  }, [requestHistory]);

  const handleDeleteSession = useCallback((sid) => {
    if (!sonnetWsConnected) return;
    console.log('🗑️ Deleting session:', sid);
    deleteSession(sid);
  }, [sonnetWsConnected, deleteSession]);

  return {
    currentPhase: streamlinedMode ? 'streamlined' : currentPhase,
    streamlinedMode,
    formData: { businessDescription },
    isFormValid: streamlinedMode ? true : isFormValid(),
    formHandlers,
    reportS3Url, reportContentCache, reportHistory, getLatestReportContent, handleViewReport,
    message, setMessage,
    finalMessages: sonnetMessages,
    sessionId: sessionId,
    sonnetTypingText, sonnetIsTyping, sonnetWsConnected, sonnetWsConnecting,
    currentStatus, pingCount, agentStatus,
    startReportGeneration, resetToForm, handleSendChatMessage,
    error, chatError, processingStage, isLoading,
    // Version control
    versionHistory, currentVersionNumber, isVersionSidebarOpen, setIsVersionSidebarOpen,
    versionContent, handleCreateVersion, handleVersionSelect, handleLoadVersionHistory,
    // URL refresh
    requestUrlRefresh, registerUrlRefreshCallback,
    // Reports
    savedReports, savedReportContent, isLoadingReports,
    handleLoadSavedReports, handleViewSavedReport,handleDeleteReport,
    // Sessions
    savedSessions, isLoadingSessions, handleLoadSessions,
    handleLoadSessionAndSwitch, handleDeleteSession,
    requestNewSession,
  };
};

export default useChatPhases;